import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, ListChecks } from 'lucide-react';
import { api } from '../api/cliente.js';

/**
 * Indicador de tarefas em segundo plano, na topbar.
 *
 * A validação de FCrDNS (e futuras ações longas) não prende a tela: vira uma
 * tarefa no servidor e continua mesmo se o operador navegar. Este indicador
 * consulta o progresso (React Query reconecta sozinho ao voltar), mostra uma
 * barra por tarefa e, ao concluir, atualiza as telas afetadas — a coluna FCrDNS
 * fica verde sem o operador recarregar nada.
 */

interface Tarefa {
  id: string;
  tipo: string;
  titulo: string;
  status: 'PENDENTE' | 'EXECUTANDO' | 'CONCLUIDA' | 'FALHOU';
  total: number;
  concluidos: number;
  resultado: { verificados?: number; problemas?: number } | null;
  erro: string | null;
  link: string | null;
  criadoEm: string;
  concluidoEm: string | null;
}

const ativa = (t: Tarefa) => t.status === 'PENDENTE' || t.status === 'EXECUTANDO';

export function TarefasIndicador() {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const vistas = useRef(new Map<string, Tarefa['status']>());
  // Tarefas que terminaram e o operador ainda não olhou (não abriu o dropdown).
  // Alimenta o badge de aviso que persiste até ele conferir.
  const [naoVistas, setNaoVistas] = useState<Map<string, 'ok' | 'problema' | 'falha'>>(new Map());

  const { data: tarefas = [] } = useQuery({
    queryKey: ['tarefas'],
    queryFn: () => api.get<Tarefa[]>('/tarefas'),
    // Enquanto houver tarefa ativa, consulta a cada 2s; senão, para.
    refetchInterval: (q) =>
      (q.state.data as Tarefa[] | undefined)?.some(ativa) ? 2000 : false,
    retry: false,
  });

  // Ao detectar que uma tarefa terminou, atualiza as telas que ela afeta e
  // marca-a como "não vista" para o badge chamar atenção.
  useEffect(() => {
    const recemConcluidas = new Map<string, 'ok' | 'problema' | 'falha'>();
    for (const t of tarefas) {
      const antes = vistas.current.get(t.id);
      vistas.current.set(t.id, t.status);
      const terminou = antes && antes !== t.status && !ativa(t);
      if (!terminou) continue;

      recemConcluidas.set(
        t.id,
        t.status === 'FALHOU' ? 'falha' : t.resultado?.problemas ? 'problema' : 'ok',
      );
      if (t.tipo === 'fcrdns') {
        void qc.invalidateQueries({ queryKey: ['ptr'] });
        void qc.invalidateQueries({ queryKey: ['enderecos'] });
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
      }
    }

    // Junta as recém-concluídas às pendentes e descarta ids que já saíram da
    // lista (podadas), para o badge não ficar preso num id que sumiu.
    setNaoVistas((atual) => {
      const presentes = new Set(tarefas.map((t) => t.id));
      const novo = new Map([...atual].filter(([id]) => presentes.has(id)));
      for (const [id, desfecho] of recemConcluidas) novo.set(id, desfecho);
      return novo.size === atual.size && [...recemConcluidas.keys()].every((id) => atual.has(id))
        ? atual
        : novo;
    });
  }, [tarefas, qc]);

  if (tarefas.length === 0) return null;

  const ativas = tarefas.filter(ativa).length;
  const naoVistasArr = [...naoVistas.values()];
  const temAviso = naoVistasArr.length > 0;
  const avisoProblema = naoVistasArr.some((d) => d !== 'ok');

  return (
    <div className="relative">
      <button
        onClick={() => {
          const abrindo = !aberto;
          setAberto(abrindo);
          // Abrir = conferir: zera o aviso de concluídas.
          if (abrindo && temAviso) setNaoVistas(new Map());
        }}
        className={`relative grid size-9 place-items-center rounded-chip hover:bg-borda-suave ${
          ativas > 0 || temAviso ? 'text-marca' : 'text-texto-suave'
        }`}
        aria-label={`Tarefas${
          ativas > 0 ? ` (${ativas} em andamento)` : temAviso ? ` (${naoVistasArr.length} concluídas)` : ''
        }`}
        title="Tarefas em segundo plano"
      >
        {ativas > 0 ? (
          <Loader2 size={18} className="animate-spin text-marca" />
        ) : temAviso ? (
          avisoProblema ? (
            <AlertTriangle size={18} className="text-aviso" />
          ) : (
            <CheckCircle2 size={18} className="text-sucesso" />
          )
        ) : (
          <ListChecks size={18} />
        )}

        {ativas > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-pill bg-marca px-1 text-[10px] font-bold leading-4 text-white">
            {ativas}
          </span>
        ) : temAviso ? (
          <span
            className={`absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-pill px-1 text-[10px] font-bold leading-4 text-white ${
              avisoProblema ? 'bg-aviso' : 'bg-sucesso'
            }`}
          >
            {naoVistasArr.length}
          </span>
        ) : null}
      </button>

      {aberto && (
        <>
          <button
            className="fixed inset-0 z-30 cursor-default"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setAberto(false)}
          />
          <div className="absolute right-0 top-11 z-40 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-borda bg-superficie shadow-lg">
            <div className="border-b border-borda-suave px-4 py-2.5">
              <p className="text-[13px] font-bold">Tarefas em segundo plano</p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {tarefas.map((t) => (
                <LinhaTarefa key={t.id} tarefa={t} aoIr={() => setAberto(false)} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LinhaTarefa({ tarefa: t, aoIr }: { tarefa: Tarefa; aoIr: () => void }) {
  const pct = t.total > 0 ? Math.min(100, Math.round((t.concluidos / t.total) * 100)) : 0;

  const corpo = (
    <div className="flex items-start gap-2.5 px-4 py-2.5">
      <span className="mt-0.5 shrink-0">
        {t.status === 'CONCLUIDA' ? (
          <CheckCircle2 size={16} className={t.resultado?.problemas ? 'text-aviso' : 'text-sucesso'} />
        ) : t.status === 'FALHOU' ? (
          <AlertTriangle size={16} className="text-erro" />
        ) : (
          <Loader2 size={16} className="animate-spin text-marca" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-semibold leading-snug">{t.titulo}</p>

        {ativa(t) ? (
          <>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-borda-suave">
              <div className="h-full rounded-full bg-marca transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-[11px] text-texto-suave">
              {t.status === 'PENDENTE' ? 'Na fila…' : `${t.concluidos}/${t.total} — ${pct}%`}
            </p>
          </>
        ) : t.status === 'CONCLUIDA' ? (
          <p className="mt-0.5 text-[11.5px] text-texto-suave">
            {t.resultado?.verificados ?? 0} verificados
            {t.resultado?.problemas
              ? ` — ${t.resultado.problemas} com problema`
              : ' — todos conferem'}
          </p>
        ) : (
          <p className="mt-0.5 text-[11.5px] text-erro">{t.erro ?? 'Falhou'}</p>
        )}
      </div>
    </div>
  );

  // Concluída com link vira atalho para a página da divergência.
  if (t.status === 'CONCLUIDA' && t.link) {
    return (
      <Link to={t.link} onClick={aoIr} className="block border-b border-borda-suave last:border-0 hover:bg-borda-tenue">
        {corpo}
      </Link>
    );
  }
  return <div className="border-b border-borda-suave last:border-0">{corpo}</div>;
}
